import { Fragment } from 'react';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import { Btn, Card, Badge } from '../ui';
import { BackButton } from '../ui';
import { IconDownload, IconPrint } from '@icons';
import { InspectFlowResponseIcon } from './InspectFlowResponseIcon';
import {
    buildReportDetailSections,
    computeReportSummaryCounts,
    formatLocationSource,
    formatReportDateTime,
    resolveReportOverallStatus,
    type ReportDetailRow,
    type ReportStatusTone,
} from './inspectionFlowReportUtils';
import type { tItems } from '../../types';
import '../../css/InspectionFlowReport.css';

type InspectionFlowReportViewProps = {
    reportTitle: string;
    reportKindLabel: string;
    regionLabel: string;
    locationLabel: string;
    isDailyServiceLog: boolean;
    submittedBy?: string | null;
    inspectionStarted?: string | null;
    inspectionCompleted?: string | null;
    createdAt?: string | null;
    currentUm?: number | null;
    lastServiceUm?: number | null;
    umSinceLastService?: number | null;
    /** Display name for the unit's measure, such as Meows or hours. */
    umLabel?: string | null;
    signatureUrl?: string | null;
    locationType?: string | null;
    mapCoordinates?: { latitude: number; longitude: number } | null;
    mapPopupTitle?: string;
    mapPopupSubtitle?: string;
    items: tItems[];
    onBack: () => void;
    backButtonText?: string;
    onPrint: () => void;
    onDownloadPdf: () => void;
    onOpenMedia: (path: string) => void;
};

const REPORT_STATUS_BADGE_CLASSES: Record<ReportStatusTone | 'under_review', string> = {
    ok: 'badge-success',
    warn: 'badge-warning',
    inop: 'badge-danger',
    under_review: 'badge-info',
    na: 'badge-neutral',
};

function reportStatusBadgeClasses(tone: ReportStatusTone | 'under_review') {
    return `${REPORT_STATUS_BADGE_CLASSES[tone]} inspect-flow-report-table-status-badge`;
}

function ReportStatusBadge({
    tone,
    label,
}: {
    tone: ReportStatusTone | 'under_review';
    label: string;
}) {
    return <Badge badgeClasses={REPORT_STATUS_BADGE_CLASSES[tone]} badgeText={label} />;
}

function ReportStatusCell({ row }: { row: ReportDetailRow }) {
    if (!row.statusLabel) {
        return <span className="inspect-flow-report-table-status-badge-placeholder">—</span>;
    }

    if (row.statusTone === 'na') {
        return (
            <Badge
                buttonSize="sm"
                badgeClasses={reportStatusBadgeClasses('na')}
                badgeText={row.statusLabel}
            />
        );
    }

    if (!row.statusTone) {
        return <span className="inspect-flow-report-table-status-badge-placeholder">{row.statusLabel}</span>;
    }

    return (
        <Badge
            buttonSize="sm"
            badgeClasses={reportStatusBadgeClasses(row.statusTone)}
            badgeText={row.statusLabel}
            badgeIcon={<InspectFlowResponseIcon tone={row.statusTone} />}
        />
    );
}

export const InspectionFlowReportView = ({
    reportTitle,
    reportKindLabel,
    regionLabel,
    locationLabel,
    isDailyServiceLog,
    submittedBy,
    inspectionStarted,
    inspectionCompleted,
    createdAt,
    currentUm,
    lastServiceUm,
    umSinceLastService,
    umLabel,
    signatureUrl,
    locationType,
    mapCoordinates,
    mapPopupTitle,
    mapPopupSubtitle,
    items,
    onBack,
    backButtonText = 'Back to Equipment',
    onPrint,
    onDownloadPdf,
    onOpenMedia,
}: InspectionFlowReportViewProps) => {
    const measureLabel = (umLabel || '').trim();
    const formatUmReading = (value: number | null | undefined) => {
        if (value == null) return '—';
        return measureLabel ? `${value.toLocaleString()} ${measureLabel}` : value.toLocaleString();
    };
    const sections = buildReportDetailSections(items);
    const counts = computeReportSummaryCounts(items);
    const overallStatus = resolveReportOverallStatus(items);
    const completedAt = inspectionCompleted || createdAt;

    return (
        <div className="inspect-flow-report-page">
            <div className="inspect-flow-report-toolbar">
                <BackButton onClick={onBack} buttonText={backButtonText} ariaLabel={backButtonText} />
                <div className="inspect-flow-report-toolbar-actions">
                    <Btn
                        variant="secondary-soft-outlined"
                        buttonSize="sm"
                        buttonIcon={<IconPrint />}
                        ariaLabel="Print report"
                        buttonOnClick={onPrint}
                    />
                    <Btn
                        variant="secondary-soft-outlined"
                        buttonSize="sm"
                        buttonIcon={<IconDownload />}
                        ariaLabel="Download PDF"
                        buttonOnClick={onDownloadPdf}
                    />
                </div>
            </div>

            <header className="inspect-flow-report-hero">
                <div className="inspect-flow-report-hero-text">
                    <p className="inspect-flow-report-eyebrow">{reportKindLabel}</p>
                    <h1 className="inspect-flow-report-title">{reportTitle}</h1>
                    <p className="inspect-flow-report-meta">
                        Region: {regionLabel} · Location: {locationLabel}
                    </p>
                </div>
                <ReportStatusBadge tone={overallStatus.tone} label={overallStatus.label} />
            </header>

            <Card variant="card-lg" className="inspect-flow-report-summary-card card-pad-0">
                <div className="inspect-flow-report-summary-grid">
                    <section className="inspect-flow-report-summary-column">
                        <h2 className="inspect-flow-report-summary-heading">
                            {isDailyServiceLog ? 'Daily Service Details' : 'Inspection Details'}
                        </h2>
                        <dl className="inspect-flow-report-summary-list">
                            {inspectionStarted && (
                                <div className="inspect-flow-report-summary-item">
                                    <dt>Started</dt>
                                    <dd>{formatReportDateTime(inspectionStarted)}</dd>
                                </div>
                            )}
                            <div className="inspect-flow-report-summary-item">
                                <dt>Completed</dt>
                                <dd>{formatReportDateTime(completedAt)}</dd>
                            </div>
                            <div className="inspect-flow-report-summary-item">
                                <dt>Submitted By</dt>
                                <dd>{submittedBy || '—'}</dd>
                            </div>
                        </dl>
                    </section>

                    <section className="inspect-flow-report-summary-column">
                        <h2 className="inspect-flow-report-summary-heading">Meter Readings</h2>
                        <dl className="inspect-flow-report-summary-list">
                            <div className="inspect-flow-report-summary-item">
                                <dt>Current UM</dt>
                                <dd>{formatUmReading(currentUm)}</dd>
                            </div>
                            <div className="inspect-flow-report-summary-item">
                                <dt>Last Service UM</dt>
                                <dd>{formatUmReading(lastServiceUm)}</dd>
                            </div>
                            <div className="inspect-flow-report-summary-item">
                                <dt>UM Since Last Service</dt>
                                <dd>{formatUmReading(umSinceLastService)}</dd>
                            </div>
                        </dl>
                    </section>

                    <section className="inspect-flow-report-summary-column">
                        <h2 className="inspect-flow-report-summary-heading">Status &amp; Signature</h2>
                        <dl className="inspect-flow-report-summary-list">
                            <div className="inspect-flow-report-summary-item">
                                <dt>Overall Status</dt>
                                <dd>
                                    <ReportStatusBadge
                                        tone={overallStatus.tone}
                                        label={overallStatus.label}
                                    />
                                </dd>
                            </div>
                            <div className="inspect-flow-report-summary-item">
                                <dt>Location Source</dt>
                                <dd>{formatLocationSource(locationType)}</dd>
                            </div>
                            <div className="inspect-flow-report-summary-item inspect-flow-report-summary-item--signature">
                                <dt>Signature</dt>
                                <dd>
                                    {signatureUrl ? (
                                        <img
                                            src={signatureUrl}
                                            alt="Submitted signature"
                                            className="inspect-flow-report-signature"
                                            crossOrigin="anonymous"
                                        />
                                    ) : (
                                        '—'
                                    )}
                                </dd>
                            </div>
                        </dl>
                    </section>
                </div>
            </Card>

            <Card variant="card-lg" className="inspect-flow-report-details-card card-pad-0">
                <div className="inspect-flow-report-details-header">
                    <h2 className="inspect-flow-report-details-title">
                        {isDailyServiceLog ? 'Daily Service Details' : 'Inspection Details'}
                    </h2>
                    <div className="inspect-flow-report-details-stats" aria-label="Report summary counts">
                        <span className="inspect-flow-report-stat tone-ok">✓ {counts.okCount} OK</span>
                        <span className="inspect-flow-report-stat tone-warn">⚠ {counts.warnCount} Warning</span>
                        <span className="inspect-flow-report-stat tone-inop">✘ {counts.inopCount} Inoperative</span>
                        <span className="inspect-flow-report-stat tone-na">{counts.naCount} N/A</span>
                    </div>
                </div>

                <div className="inspect-flow-report-table-wrap">
                    <table className="inspect-flow-report-table">
                        <thead>
                            <tr>
                                <th scope="col">Prompt</th>
                                <th scope="col">Section</th>
                                <th scope="col">Description</th>
                                <th scope="col">Status</th>
                                <th scope="col">Notes</th>
                                <th scope="col">Media</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sections.map((section) => (
                                <Fragment key={section.title}>
                                    <tr className="inspect-flow-report-section-row">
                                        <th scope="colgroup" colSpan={6}>
                                            {section.title}
                                        </th>
                                    </tr>
                                    {section.rows.map((row) => (
                                        <tr key={row.key}>
                                            <td>{row.prompt}</td>
                                            <td>{row.section}</td>
                                            <td>{row.description || '—'}</td>
                                            <td className="inspect-flow-report-status-td">
                                                <ReportStatusCell row={row} />
                                            </td>
                                            <td>{row.notes || '—'}</td>
                                            <td>
                                                {row.photos.length ? (
                                                    <div className="inspect-flow-report-media-links">
                                                        {row.photos.map((photo) => (
                                                            <Btn
                                                                key={photo}
                                                                variant="link"
                                                                buttonText="View"
                                                                buttonOnClick={() => onOpenMedia(photo)}
                                                            />
                                                        ))}
                                                    </div>
                                                ) : (
                                                    '—'
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            {mapCoordinates && (
                <Card variant="card-lg" className="inspect-flow-report-map-card card-pad-0">
                    <h2 className="inspect-flow-report-map-title">Map</h2>
                    <div className="inspect-flow-report-map-wrap">
                        <MapContainer
                            center={[mapCoordinates.latitude, mapCoordinates.longitude]}
                            zoom={13}
                            scrollWheelZoom={false}
                        >
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                            <Marker position={[mapCoordinates.latitude, mapCoordinates.longitude]}>
                                <Popup>
                                    <div>{mapPopupTitle}</div>
                                    {mapPopupSubtitle && <div>{mapPopupSubtitle}</div>}
                                </Popup>
                            </Marker>
                        </MapContainer>
                    </div>
                </Card>
            )}
        </div>
    );
};
